import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { adminAuth } from '../../services/adminAuth';
import { Settings as SettingsIcon, Save, LogOut, DollarSign, Mail, Database, Shield, Bell } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const ctx = useOutletContext();
  const navigate = useNavigate();
  const user = adminAuth.getUser();
  const [config, setConfig] = useState({
    deposit_amount: 30,
    custom_date_surcharge: 55,
    cancellation_fee: 0,
    sendgrid_enabled: true,
    stripe_enabled: true,
    bubble_enabled: false,
    notifications_email: true,
    notifications_sms: false,
  });

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/admin/login', { replace: true });
  };

  const save = () => toast.success('Settings saved');

  const Section = ({ title, icon: Icon, children }) => (
    <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5`}>
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-400" /> {title}
      </h3>
      {children}
    </div>
  );

  const Toggle = ({ label, checked, onChange }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-600'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
    </div>
  );

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Settings</h1>
        <p className={`text-sm ${ctx.textSecondary} mt-1`}>Operational configuration and integrations</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Pricing */}
        <Section title="Pricing & Fees" icon={DollarSign}>
          <div className="space-y-3">
            <div>
              <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Reservation Deposit ($)</label>
              <input type="number" value={config.deposit_amount} onChange={e => setConfig({ ...config, deposit_amount: +e.target.value })}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} data-testid="setting-deposit" />
            </div>
            <div>
              <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Custom Date Surcharge ($)</label>
              <input type="number" value={config.custom_date_surcharge} onChange={e => setConfig({ ...config, custom_date_surcharge: +e.target.value })}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
            </div>
            <div>
              <label className={`text-xs ${ctx.textSecondary} block mb-1`}>Cancellation Fee ($)</label>
              <input type="number" value={config.cancellation_fee} onChange={e => setConfig({ ...config, cancellation_fee: +e.target.value })}
                className={`w-full px-3 py-2 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30`} />
            </div>
          </div>
        </Section>

        {/* Integrations */}
        <Section title="Integrations" icon={Database}>
          <Toggle label="Stripe Payments" checked={config.stripe_enabled} onChange={v => setConfig({ ...config, stripe_enabled: v })} />
          <Toggle label="SendGrid Email" checked={config.sendgrid_enabled} onChange={v => setConfig({ ...config, sendgrid_enabled: v })} />
          <Toggle label="Bubble.io Backend (requires subscription)" checked={config.bubble_enabled} onChange={v => setConfig({ ...config, bubble_enabled: v })} />
          <p className={`text-xs ${ctx.textSecondary} mt-3 pt-3 border-t ${ctx.borderColor}`}>
            Bubble integration is disabled. Backend uses mock data layer. Upgrade your Bubble.io plan to enable live sync.
          </p>
        </Section>

        {/* Notifications */}
        <Section title="Notifications" icon={Bell}>
          <Toggle label="Email Notifications" checked={config.notifications_email} onChange={v => setConfig({ ...config, notifications_email: v })} />
          <Toggle label="SMS Notifications" checked={config.notifications_sms} onChange={v => setConfig({ ...config, notifications_sms: v })} />
        </Section>

        {/* Account */}
        <Section title="Account & Security" icon={Shield}>
          <div className="space-y-3">
            <div>
              <p className={`text-xs ${ctx.textSecondary}`}>Signed in as</p>
              <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
              <p className={`text-xs ${ctx.textSecondary}`}>{user?.email}</p>
            </div>
            <div className={`pt-3 border-t ${ctx.borderColor}`}>
              <p className={`text-xs ${ctx.textSecondary}`}>Role</p>
              <p className="text-sm font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 text-sm font-medium w-full justify-center" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </Section>
      </div>

      <div className="flex justify-end">
        <button onClick={save} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="settings-save">
          <Save className="w-4 h-4" /> Save Settings
        </button>
      </div>
    </div>
  );
}
